import { post, request } from '../api';
import type { TaskBusiness } from './tasks';

/** Mirrors the view types of `apps/api/src/agents/*`; money always crosses the wire as a string. */

export type SupervisionLevel = 'every_step' | 'plan_and_irreversible' | 'plan_only';
export type LibraryStatus = 'draft' | 'active' | 'deprecated' | 'archived';
export type SideEffect = 'none' | 'reversible' | 'irreversible';

export interface AgentTemplate {
  id: string;
  slug: string;
  name: string;
  purpose: string;
  baseInstruction: string;
  defaultTools: string[];
  defaultSupervision: SupervisionLevel;
  defaultModel: string | null;
  currentVersion: number;
  status: LibraryStatus;
  shipped: boolean;
  instanceCount: number;
  updatedAt: string;
}

export interface AgentOverrides {
  instructionAdditions?: string;
  tone?: string;
  language?: string;
  credentialId?: string;
}

export interface AgentInstance {
  id: string;
  businessId: string | null;
  business: { id: string; name: string } | null;
  templateId: string;
  template: { id: string; slug: string; name: string; status: LibraryStatus; currentVersion: number };
  templateVersion: number;
  name: string;
  model: string;
  supervisionLevel: SupervisionLevel;
  overrides: AgentOverrides;
  status: LibraryStatus;
  version: number;
  actorId: string | null;
  policyId: string | null;
  policyVersion: number | null;
  spentThisMonth: string;
  budget: { soft: string; hard: string; currency: string } | null;
  createdAt: string;
  updatedAt: string;
}

export interface AgentPolicy {
  id: string | null;
  scope: 'office' | 'instance';
  allowedToolIds: string[];
  maxCostPerRun: string | null;
  maxCostPerDay: string | null;
  maxToolCalls: number | null;
  maxExecutionTimeSec: number | null;
  maxRetries: number;
  approvalThreshold: string | null;
  version: number;
  officeAllowedToolIds?: string[];
}

export interface AgentBudget {
  id: string | null;
  periodStart: string;
  periodEnd: string;
  softLimit: string;
  hardLimit: string;
  currency: string;
  spent: string;
}

export interface AgentTool {
  id: string;
  mcpServer: string;
  toolName: string;
  ref: string;
  description: string;
  sideEffect: SideEffect;
  costClass: string;
  idempotent: boolean;
  enabled: boolean;
  updatedAt: string;
}

export interface AgentModel {
  id: string;
  provider: string;
  model: string;
  inputPerMTok: string;
  outputPerMTok: string;
  cachedPerMTok: string;
  currency: string;
  enabled: boolean;
  shipped: boolean;
  updatedAt: string;
}

export interface CredentialOption {
  id: string;
  provider: string;
  name: string;
}

export interface CreateInstanceInput {
  templateId: string;
  businessId: string;
  name: string;
  model: string;
  credentialId?: string | null;
  overrides?: AgentOverrides;
  supervisionLevel?: SupervisionLevel;
  budget?: { softLimit: string; hardLimit: string; currency?: string };
}

export interface UpdateInstanceInput {
  version: number;
  name?: string;
  model?: string;
  overrides?: AgentOverrides;
  supervisionLevel?: SupervisionLevel;
  status?: LibraryStatus;
  changeNote?: string;
}

export interface PolicyInput {
  allowedToolIds: string[];
  maxCostPerRun?: string | null;
  maxCostPerDay?: string | null;
  maxToolCalls?: number | null;
  maxExecutionTimeSec?: number | null;
  maxRetries?: number;
  approvalThreshold?: string | null;
}

export interface ModelInput {
  provider: string;
  model: string;
  inputPerMTok: string;
  outputPerMTok: string;
  cachedPerMTok?: string;
  currency?: string;
  enabled?: boolean;
}

const id = (v: string) => encodeURIComponent(v);
const put = <T,>(path: string, body: unknown) => request<T>(path, { method: 'PUT', body: JSON.stringify(body) });
const patch = <T,>(path: string, body: unknown) => request<T>(path, { method: 'PATCH', body: JSON.stringify(body) });

export const agentsApi = {
  businesses: () => request<TaskBusiness[]>('/agents/businesses'),
  credentials: (provider?: string) => request<CredentialOption[]>(`/agents/credentials${provider ? `?provider=${encodeURIComponent(provider)}` : ''}`),

  templates: () => request<AgentTemplate[]>('/agents/templates'),
  activateTemplate: (templateId: string) => post<AgentTemplate>(`/agents/templates/${id(templateId)}/activate`),

  instances: (businessId?: string) => request<AgentInstance[]>(`/agents/instances${businessId ? `?businessId=${encodeURIComponent(businessId)}` : ''}`),
  createInstance: (input: CreateInstanceInput) => post<AgentInstance>('/agents/instances', input),
  updateInstance: (instanceId: string, input: UpdateInstanceInput) => patch<AgentInstance>(`/agents/instances/${id(instanceId)}`, input),

  instancePolicy: (instanceId: string) => request<AgentPolicy>(`/agents/instances/${id(instanceId)}/policy`),
  putInstancePolicy: (instanceId: string, input: PolicyInput) => put<AgentPolicy>(`/agents/instances/${id(instanceId)}/policy`, input),
  officePolicy: () => request<AgentPolicy>('/agents/policy/office'),
  putOfficePolicy: (input: PolicyInput) => put<AgentPolicy>('/agents/policy/office', input),

  budget: (instanceId: string) => request<AgentBudget>(`/agents/instances/${id(instanceId)}/budget`),
  putBudget: (instanceId: string, input: { softLimit: string; hardLimit: string; currency?: string }) => put<AgentBudget>(`/agents/instances/${id(instanceId)}/budget`, input),

  tools: () => request<AgentTool[]>('/agents/tools'),
  setToolEnabled: (toolId: string, enabled: boolean) => patch<AgentTool>(`/agents/tools/${id(toolId)}`, { enabled }),

  models: () => request<AgentModel[]>('/agents/models'),
  createModel: (input: ModelInput) => post<AgentModel>('/agents/models', input),
  updateModel: (modelId: string, input: Partial<Omit<ModelInput, 'provider' | 'model'>>) => patch<AgentModel>(`/agents/models/${id(modelId)}`, input),
};
